"use client";

import { useEffect } from "react";
import { useParams } from "next/navigation";
import { Button, Typography } from "@boostdev/design-system-components/client";
import { cn } from "@boostdev/design-system-components/utils";
import { useClientTranslation } from "@/lib/i18n/client";
import { defaultNS, fallbackLng } from "@/lib/i18n/config";
import css from "./error.module.css";

export interface ErrorBoundaryProps {
  readonly error: Error & { digest?: string };
  readonly reset: () => void;
}

/**
 * Segment-level error boundary for every localised route.
 *
 * Without it a client-side exception (a hydration crash, for instance) tears
 * the whole tree down to Next's unstyled, untranslated default screen. The
 * collapsed details block surfaces the message for visitors who cannot open
 * devtools — the Edge crash only reproduces with the inspector closed.
 */
export default function ErrorBoundary({
  error,
  reset,
}: ErrorBoundaryProps): React.ReactElement {
  const params = useParams<{ locale?: string }>();
  const locale = params?.locale ?? fallbackLng;
  // `useSuspense: false` is load-bearing: react-i18next suspends while the
  // locale chunk loads, and a boundary that suspends renders nothing at all.
  // Every `t()` below therefore carries a defaultValue for that first paint.
  const { t } = useClientTranslation(locale, defaultNS, {
    useSuspense: false,
  });

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div className={cn(["utl-page-grid", css.page])}>
      <main className={cn(["utl-main-grid", css.panel])}>
        <div className={css.content}>
          <Typography variant="h1" component="h1">
            {t("common:error.title", { defaultValue: "Something went wrong" })}
          </Typography>
          <Typography variant="body">
            {t("common:error.body", {
              defaultValue: "This page could not be loaded.",
            })}
          </Typography>
          <div className={css.actions}>
            <Button type="button" onClick={reset}>
              {t("common:error.retry", { defaultValue: "Try again" })}
            </Button>
            <Button variant="outline" href={`/${locale}`}>
              {t("common:error.home", { defaultValue: "Home" })}
            </Button>
          </div>
          <details className={css.details}>
            <summary className={css.summary}>
              <Typography variant="body_s" component="span">
                {t("common:error.details", { defaultValue: "Details" })}
              </Typography>
            </summary>
            <Typography variant="body_s" component="pre" className={css.digest}>
              {error.digest ? `${error.digest}\n` : ""}
              {error.message}
            </Typography>
          </details>
        </div>
      </main>
    </div>
  );
}
